import { createSelector } from 'reselect';

import { FAVORITE, TYPES } from 'constants/FilterConstants';
import { PATH } from 'constants/RoutesConstants';
import { getSelectedGroupId } from 'modules/groups/selectors';
import { getLocation } from 'modules/router/selectors';
import { getCurrentUserId } from 'modules/users/selectors/currentUser';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { getProps } from 'utils/selectors';

import { DEALER_MODE, GROUP_MODE, USER_MODE } from './constants';

const getRoot = state => state;

/** @typedef {{mediaLibrary: import('./reducer').MediaLibraryReducer}} State */
/** @type {(state: State) => import('./reducer').MediaLibraryReducer}   */
const getMediaLibrary = state => state.mediaLibrary;

const getData = createSelector(getMediaLibrary, ({ data }) => data);

const getUI = createSelector(getMediaLibrary, ({ ui }) => ui);

const getDashboard = createSelector(getUI, ({ dashboard }) => dashboard);

export const getMediaMode = createSelector(getLocation, location => {
  if (location.pathname === PATH.MEDIA_LIBRARY_DEALER.path) {
    return DEALER_MODE;
  }
  if (location.pathname === PATH.MEDIA_LIBRARY_GROUP.path) {
    return GROUP_MODE;
  }
  return USER_MODE;
});

export const isMediaLibraryLocation = createSelector(
  getLocation,
  location =>
    location.pathname === PATH.MEDIA_LIBRARY_DEALER.path ||
    location.pathname === PATH.MEDIA_LIBRARY_GROUP.path ||
    location.pathname === PATH.MEDIA_LIBRARY_PERSONAL.path
);

export const getFilters = createSelector(getUI, getMediaMode, ({ filters }, mode) => filters[mode]);

export const getFiltersByType = createSelector(getFilters, getProps, (filters, type) => filters[type]);

// eslint-disable-next-line no-confusing-arrow
const getFormattedType = createSelector(getProps, getFilters, (intl, filters) =>
  filters[TYPES].length > 0
    ? {
      id: TYPES,
      name: intl.formatMessage({ id: 'page.keyDeposit.planning.filters.types', defaultMessage: 'Types' }),
      values: filters[TYPES].map(filter => ({
        value: filter,
        name: intl.formatMessage({ id: `mediaLibrary.fileType.${filter}`, defaultMessage: filter }),
        className: 'default',
      })),
    }
    : undefined);

// eslint-disable-next-line no-confusing-arrow
const getFormattedFavorite = createSelector(getProps, getFilters, (intl, filters) =>
  filters[FAVORITE]
    ? {
      id: FAVORITE,
      name: intl.formatMessage({ id: 'mediaLibrary.filterFavorite', defaultMessage: 'Only favourites' }),
      values: [
        {
          value: filters[FAVORITE],
          name: intl.formatMessage({ id: 'yes', defaultMessage: 'Yes' }),
          className: 'default',
        },
      ],
    }
    : undefined);

export const getFormattedFilters = createSelector(getFormattedType, getFormattedFavorite, (type, favorite) => [
  ...(type ? [type] : []),
  ...(favorite ? [favorite] : []),
]);

export const getFilterCount = createSelector(
  getFormattedFilters,
  filters => filters.reduce((acc, { values = [] }) => acc + values.length, 0) ?? 0
);

export const getDefaultFetchParamsByMode = createSelector(
  getProps,
  getDashboard,
  getSelectedDealerId,
  getSelectedGroupId,
  getCurrentUserId,
  (mode, dashboard, dealerId, groupId, userId) => {
    const { size, number: page } = dashboard[mode].page;
    if (mode === DEALER_MODE) {
      return { dealerId, size, page, scope: 'DEALER' };
    }
    if (mode === GROUP_MODE) {
      return { groupId, size, page, scope: 'GROUP' };
    }
    if (mode === USER_MODE) {
      return { userId, size, page, scope: 'USER' };
    }
    return { size, page, dealerId, groupId, userId, scope: 'CONTEXT' };
  }
);

export const getDefaultFetchParams = createSelector(getRoot, getMediaMode, getDefaultFetchParamsByMode);

export const getFetchParams = createSelector(
  getFilters,
  getDefaultFetchParams,
  (filters, defaultParams) =>
    Object.entries(filters).reduce((acc, [id, value]) => {
      if ((Array.isArray(value) && value.length === 0) || !value) {
        return acc;
      }
      return { ...acc, [id]: value };
    }, defaultParams)

);

export const hasMoreByMode = createSelector(getDashboard, getProps, (dashboard, mode) => dashboard[mode].hasMore);
export const isLoadingByMode = createSelector(getDashboard, getProps, (dashboard, mode) => dashboard[mode].loading);
export const isLoading = createSelector(getRoot, getMediaMode, isLoadingByMode);

export const getApiErrorByMode = createSelector(getDashboard, getProps, (dashboard, mode) => dashboard[mode].error);

export const getMedias = createSelector(
  getData,
  getMediaMode,
  getDashboard,
  ({ entities }, mode, dashboard) => dashboard[mode].ids.map(id => entities[id])

);

export const getSortedMediasByMode = createSelector(getData, getDashboard, getProps, (data, dashboard, mode) =>
  dashboard[mode].ids.map(id => data.entities[id]).sort((a, b) => b.favorite - a.favorite));
export const getMediaById = createSelector(getData, getProps, ({ entities }, id) => entities[id]);

export const isMediaDrawerVisibleSelector = createSelector(getUI, ({ isMediaDrawerVisible }) => isMediaDrawerVisible);

export const isUpdating = createSelector(getUI, ui => ui.loading.isUpdating);
export const isDeleting = createSelector(getUI, ui => ui.loading.isDeleting);
export const shouldDisableActions = createSelector(getUI, ui => Object.values(ui.loading).some(bool => bool));

export const isUploadModalVisibleSelector = createSelector(getUI, ({ isUploadModalVisible }) => isUploadModalVisible);
